import React, { type ChangeEventHandler, Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import type { FormErrors } from '@jwp/ott-common/types/form';
import type { CustomFormField, RegistrationFormData } from '@jwp/ott-common/types/account';
import { testId } from '@jwp/ott-common/src/utils/common';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import Visibility from '@jwp/ott-theme/assets/icons/visibility.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/visibility_off.svg?react';
import env from '@jwp/ott-common/src/env';

import TextField from '../TextField/TextField';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import PasswordStrength from '../PasswordStrength/PasswordStrength';
import CustomRegisterField from '../CustomRegisterField/CustomRegisterField';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Link from '../Link/Link';
import Icon from '../Icon/Icon';
import { modalURLFromLocation } from '../../utils/location';
import Checkbox from '../Checkbox/Checkbox';
import { useTheme } from '../../../../../ThemeContext';

import styles from './RegistrationForm.module.scss';

type Props = {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onConsentChange: Dispatch<SetStateAction<Record<string, string | boolean>>>;
  errors: FormErrors<RegistrationFormData>;
  values: RegistrationFormData;
  loading: boolean;
  consentValues: Record<string, string | boolean>;
  consentErrors: string[];
  submitting: boolean;
  validationError?: boolean;
  publisherConsents: CustomFormField[] | null;
};

const RegistrationForm: React.FC<Props> = ({
  onSubmit,
  onChange,
  onBlur,
  values,
  errors,
  submitting,
  validationError,
  loading,
  publisherConsents,
  consentValues,
  onConsentChange,
  consentErrors,
}: Props) => {
  const [viewPassword, toggleViewPassword] = useToggle();
  const { universalStyles } = useTheme();
  const { t, i18n } = useTranslation('account');
  const location = useLocation();

  const ref = useRef<HTMLDivElement>(null);
  const htmlLang = i18n.language !== env.APP_DEFAULT_LANGUAGE ? env.APP_DEFAULT_LANGUAGE : undefined;

  const formatConsentLabel = (label: string): string | JSX.Element => {
    const sanitizedLabel = DOMPurify.sanitize(label);
    const hasHrefOpenTag = /<a(.|\n)*?>/.test(sanitizedLabel);
    const hasHrefCloseTag = /<\/a(.|\n)*?>/.test(sanitizedLabel);

    if (hasHrefOpenTag && hasHrefCloseTag) {
      return <span dangerouslySetInnerHTML={{ __html: label }} />;
    }

    return label;
  };

  useEffect(() => {
    if (errors.form) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }, [errors.form]);

  return (
    <form onSubmit={onSubmit} data-testid={testId('registration-form')} noValidate>
      <h2 className={styles.title} style={universalStyles.signInText}>
        {t('registration.sign_up')}
      </h2>
      <div ref={ref}>
        {errors.form ? (
          <FormFeedback variant="error" visible={!validationError}>
            {errors.form}
          </FormFeedback>
        ) : null}
      </div>
      <TextField
        value={values.firstName}
        onChange={onChange}
        onBlur={onBlur}
        label={t('personal_details.fist_name')}
        placeholder={t('registration.first_name_placeholder')}
        error={!!errors.firstName}
        helperText={errors.firstName}
        name="firstName"
        type="text"
        required
        customStyle={universalStyles.emailIdText}
      />
      <TextField
        value={values.lastName}
        onChange={onChange}
        onBlur={onBlur}
        label={t('personal_details.last_name')}
        placeholder={t('registration.last_name_placeholder')}
        error={!!errors.lastName}
        helperText={errors.lastName}
        name="lastName"
        type="text"
        required
        customStyle={universalStyles.emailIdText}
      />
      <TextField
        value={values.email}
        onChange={onChange}
        onBlur={onBlur}
        label={t('registration.email')}
        placeholder={t('registration.email_placeholder')}
        error={!!errors.email}
        helperText={errors.email}
        name="email"
        type="email"
        autoComplete="email"
        required
        customStyle={universalStyles.emailIdText}
      />
      <TextField
        value={values.password}
        onChange={onChange}
        onBlur={onBlur}
        label={t('registration.password')}
        placeholder={t('registration.password_placeholder')}
        error={!!errors.password}
        customStyle={universalStyles.emailIdText}
        helperText={
          <React.Fragment>
            <PasswordStrength password={values.password} />
            {!!errors.password && t('registration.password_helper_text')}
          </React.Fragment>
        }
        name="password"
        type={viewPassword ? 'text' : 'password'}
        rightControl={
          <IconButton aria-label={t('registration.view_password')} onClick={() => toggleViewPassword()} aria-pressed={viewPassword}>
            <Icon icon={viewPassword ? Visibility : VisibilityOff} />
          </IconButton>
        }
        autoComplete="new-password"
        required
      />
      <TextField
        value={values.confirmPassword}
        onChange={onChange}
        onBlur={onBlur}
        label={t('registration.confirm_password')}
        placeholder={t('registration.confirm_password_placeholder')}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        name="confirmPassword"
        type={viewPassword ? 'text' : 'password'}
        customStyle={universalStyles.emailIdText}
        rightControl={
          <IconButton aria-label={t('registration.view_password')} onClick={() => toggleViewPassword()} aria-pressed={viewPassword}>
            <Icon icon={viewPassword ? Visibility : VisibilityOff} />
          </IconButton>
        }
        autoComplete="new-password"
        required
      />

      <Checkbox
        name="termsAgreed"
        label={
          <>
            <span style={{ fontWeight: '400', fontSize: '12px', ...universalStyles.neutral400 }}>
              {t('registration.terms_content_part1')}{' '}
              <a href="/terms-of-use" style={{ textDecoration: 'none' }}>
                {' '}
                {t('registration.terms_content_part2')}{' '}
              </a>
              {t('registration.terms_content_part3')}{' '}
              <a href="/privacy-policy" style={{ textDecoration: 'none' }}>
                {t('registration.terms_content_part4')}
              </a>
            </span>
          </>
        }
        checked={values.termsAgreed.toString() === 'true' || false}
        onChange={(e: any) => {
          onChange(e);
          publisherConsents && onConsentChange({ terms: e.target.checked });
        }}
        // required
        error={!!errors.termsAgreed}
        helperText={errors.termsAgreed}
      />

      {/* {publisherConsents && (
        <div className={styles.customFields} data-testid="custom-reg-fields">
          {publisherConsents.map((consent) => {
            const consentError = consentErrors.find((error) => error === consent.name);

            return (
              <CustomRegisterField
                key={consent.name}
                type={consent.type}
                name={consent.name}
                options={consent.options}
                label={formatConsentLabel(consent.label)}
                placeholder={consent.placeholder}
                value={consentValues[consent.name] || ''}
                required={consent.required}
                error={!!consentError}
                helperText={consentErrors?.includes(consent.name) ? t('registration.consent_required', { field: consent.name }) : undefined}
                onChange={onConsentChange}
                lang={htmlLang}
              />
            );
          })}
        </div>
      )}
       */}
      <Button
        className={styles.continue}
        type="submit"
        label={t('registration.sign_up_button')}
        variant="contained"
        color="primary"
        size="exlarge"
        disabled={submitting}
        fullWidth
        textStyle={universalStyles.primaryThemeButtonText}
      />
      <p className={styles.bottom} style={universalStyles.bottomSubText}>
        {t('registration.already_account')}{' '}
        <Link className={styles.signinlink} to={modalURLFromLocation(location, 'login')} customTextStyle={universalStyles.signOrSignUpLinkHighlight}>
          {t('login.sign_in')}
        </Link>
      </p>
      {(loading || submitting) && <LoadingOverlay transparentBackground inline />}
    </form>
  );
};

export default RegistrationForm;
